import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { Suggestion } from './types';

type RegisteredSuggestion = Suggestion & {
  id: string;
};

type SuggestionHandler<S extends Suggestion = Suggestion> = (
  suggestion: S,
  /** allows the handler to redirect */
  setRedirectUrl: (url: string) => void,
) => void;

interface RegisteredSuggestionGroup {
  loading: boolean;
  suggestions: Suggestion[];
  handler?: SuggestionHandler;
}

interface OmnibarContextValue {
  suggestions: RegisteredSuggestion[];
  registerSuggestions: <CustomSuggestion extends Suggestion>(props: {
    id: string;
    loading: boolean;
    suggestions: CustomSuggestion[];
    handler?: SuggestionHandler<CustomSuggestion>;
  }) => void;
  unregisterSuggestions: (id: string) => void;
  getHandler: (id: string) => SuggestionHandler | undefined;
  searchValue: string;
  setSearchValue: (value: string) => void;
  loading: boolean;
}

const OmnibarContext = createContext<OmnibarContextValue | undefined>(
  undefined,
);

interface OmnibarProviderProps {
  children: ReactNode;
}

export const OmnibarProvider: React.FC<OmnibarProviderProps> = ({
  children,
}) => {
  const [registeredSuggestions, setRegisteredSuggestions] = useState<
    Map<string, RegisteredSuggestionGroup>
  >(new Map());

  const [searchValue, setSearchValue] = useState<string>('');

  const registerSuggestions = useCallback(
    <CustomSuggestion extends Suggestion>({
      id,
      loading,
      suggestions,
      handler,
    }: {
      id: string;
      loading: boolean;
      suggestions: CustomSuggestion[];
      handler?: SuggestionHandler<CustomSuggestion>;
    }) => {
      setRegisteredSuggestions(prev => {
        const newMap = new Map(prev);
        newMap.set(id, {
          loading,
          suggestions,
          handler: handler as SuggestionHandler<Suggestion>,
        });
        return newMap;
      });
    },
    [],
  );

  const unregisterSuggestions = useCallback((id: string) => {
    setRegisteredSuggestions(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  }, []);

  const getHandler = useCallback(
    (id: string): SuggestionHandler | undefined => {
      return registeredSuggestions.get(id)?.handler;
    },
    [registeredSuggestions],
  );

  const suggestions: RegisteredSuggestion[] = React.useMemo(() => {
    const allSuggestions: RegisteredSuggestion[] = [];
    registeredSuggestions.forEach(({ suggestions }, id) => {
      suggestions.forEach(suggestion => {
        allSuggestions.push({
          ...suggestion,
          id,
        });
      });
    });
    return allSuggestions;
  }, [registeredSuggestions]);

  const loading = React.useMemo(() => {
    return Object.values(registeredSuggestions).some(group => group.loading);
  }, [registeredSuggestions]);

  const contextValue = useMemo<OmnibarContextValue>(
    () => ({
      suggestions,
      registerSuggestions,
      unregisterSuggestions,
      getHandler,
      searchValue,
      setSearchValue,
      loading,
    }),
    [
      suggestions,
      registerSuggestions,
      unregisterSuggestions,
      getHandler,
      searchValue,
      setSearchValue,
      loading,
    ],
  );

  return (
    <OmnibarContext.Provider value={contextValue}>
      {children}
    </OmnibarContext.Provider>
  );
};

export const useOmnibarContext = (): OmnibarContextValue => {
  const context = useContext(OmnibarContext);
  if (!context) {
    throw new Error('useOmnibarContext must be used within an OmnibarProvider');
  }
  return context;
};

export const useRegisterOmnibarSuggestions = <
  CustomSuggestion extends Suggestion<T>,
  T = unknown,
>({
  id,
  loading,
  suggestions,
  handler,
}: {
  id: string;
  loading: boolean;
  suggestions: CustomSuggestion[];
  handler?: SuggestionHandler<CustomSuggestion>;
}) => {
  const { registerSuggestions, unregisterSuggestions } = useOmnibarContext();

  React.useEffect(() => {
    registerSuggestions({ id, loading, suggestions, handler });
    return () => {
      unregisterSuggestions(id);
    };
  }, [
    id,
    loading,
    suggestions,
    handler,
    registerSuggestions,
    unregisterSuggestions,
  ]);
};
